import { TrackingType } from '../../../Commerce/TrackingType.csharp';
import { RecommendationsRepository } from '../../Recommendations/RecommendationsRepository';
import { BrandTrackingDetails } from './BrandTrackingDetails.csharp';
import { CategoryTrackingDetails } from './CategoryTrackingDetails.csharp';
import { EpiTrackingData } from './EpiTrackingData';
import { ProductTrackingDetails } from './ProductTrackingDetails.csharp';
import { SearchTrackingDetails } from './SearchTrackingDetails.csharp';
import { TrackingDetails } from './TrackingDetails.csharp';
import { epiRecommendations, TrackingDataFactory } from './VisitorIntelligenceTrackingScript';

declare let VisitorIntelligenceTrackingEnabled: boolean;
declare let RecommendationsRequired: boolean;
declare let VisitorIntelligenceSettings: object;

export interface WidgetConfiguration {
    area: string;
    maximumNumberOfItems: number;
}

export class VisitorIntelligenceFacade {
    private static MAX_TERM_LENGTH = 235;
    private static IsEnabled: boolean =
        typeof VisitorIntelligenceTrackingEnabled !== 'undefined' && typeof TrackingDataFactory !== 'undefined' && typeof window !== 'undefined'
            ? VisitorIntelligenceTrackingEnabled
            : false;

    private static AttachRecommendations: boolean =
        typeof RecommendationsRequired !== 'undefined' && typeof window !== 'undefined' ? RecommendationsRequired : false;

    private static recommendationsTracker = VisitorIntelligenceFacade.IsEnabled ? epiRecommendations(VisitorIntelligenceSettings) : undefined;

    public static Track(trackingDetails: TrackingDetails) {
        if (!this.IsEnabled || trackingDetails.skipTracking) {
            return;
        }

        switch (trackingDetails.type) {
            case TrackingType.Home: {
                VisitorIntelligenceFacade.TrackHomePage();
                break;
            }
            case TrackingType.Brand: {
                VisitorIntelligenceFacade.TrackBrandPage(trackingDetails as BrandTrackingDetails);
                break;
            }
            case TrackingType.Product: {
                VisitorIntelligenceFacade.TrackProductPage(trackingDetails as ProductTrackingDetails);
                break;
            }
            case TrackingType.Checkout: {
                VisitorIntelligenceFacade.TrackCheckoutPage();
                break;
            }
            case TrackingType.Basket: {
                VisitorIntelligenceFacade.TrackCartPage();
                break;
            }
            case TrackingType.Category: {
                VisitorIntelligenceFacade.TrackCategoryPage(trackingDetails as CategoryTrackingDetails);
                break;
            }
            case TrackingType.Wishlist: {
                VisitorIntelligenceFacade.TrackFavouritesPage();
                break;
            }
            case TrackingType.SearchResults: {
                VisitorIntelligenceFacade.TrackSearchPage(trackingDetails as SearchTrackingDetails);
                break;
            }
            case TrackingType.Order:
                throw new Error('Use dedicated methods instead');
            case TrackingType.Attribute:
            case TrackingType.Other: {
                break;
            }
        }
    }

    public static TrackSearchResultsPage(term: string, productCodes: string[], totalResults: number) {
        if (!this.IsEnabled) {
            return;
        }

        if (!term) {
            return;
        }

        const truncatedTerm = term.substring(0, this.MAX_TERM_LENGTH);

        const data = TrackingDataFactory.createSearchResultTrackingData(truncatedTerm, productCodes, totalResults);
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    public static TrackSearchPage(trackingDetails: SearchTrackingDetails) {
        this.TrackSearchResultsPage(trackingDetails.term, trackingDetails.productCodes, trackingDetails.totalResults);
    }

    public static TrackOrderConfirmationPage(orderId: string) {
        if (!this.IsEnabled) {
            return;
        }

        const data = TrackingDataFactory.createOrderTrackingData(orderId);
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    private static TrackHomePage() {
        const data = TrackingDataFactory.createHomeTrackingData();
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    private static TrackCheckoutPage() {
        const data = TrackingDataFactory.createCheckoutTrackingData();
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    private static TrackCartPage() {
        const data = TrackingDataFactory.createBasketTrackingData();
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    private static TrackCategoryPage(trackingDetails: CategoryTrackingDetails) {
        const data = TrackingDataFactory.createCategoryTrackingData(trackingDetails.categoryCode);
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    private static TrackFavouritesPage() {
        const data = TrackingDataFactory.createWishlistTrackingData();
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    private static TrackBrandPage(trackingDetails: BrandTrackingDetails) {
        const data = TrackingDataFactory.createBrandTrackingData(trackingDetails.brandName);
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    private static TrackProductPage(trackingDetails: ProductTrackingDetails) {
        const urlParams = new URLSearchParams(window.location.search);
        const recommendationId = urlParams.get('recommendationId');

        const data = TrackingDataFactory.createProductTrackingData(trackingDetails.productCode, trackingDetails.productName);
        if (recommendationId) {
            data.recommendationId = recommendationId;
        }
        VisitorIntelligenceFacade.TrackInternal(data);
    }

    private static TrackInternal(data: EpiTrackingData, widgetOptions: WidgetConfiguration[] | object = {}) {
        data.skipRecommendations = !VisitorIntelligenceFacade.AttachRecommendations;

        if (VisitorIntelligenceFacade.recommendationsTracker) {
            VisitorIntelligenceFacade.recommendationsTracker.track(data, 'default', 'web', RecommendationsRepository.populateRepository, widgetOptions);
        }
    }
}
